import {
  Button,
  EmojiObjectsIcon,
  Grid,
  makeStyles,
  StatusBox,
  TextField,
  Theme,
  Typography,
  XDatePicker as DatePicker,
} from '@freighthub/silhouette-core'
import { FC, useEffect, useState } from 'react'
import { useController, useFieldArray, useWatch } from 'react-hook-form'
import { formatNumber } from 'service/NumberFormatter'
import { UnleashFeatureFlagKeys, useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'
import { useI18n } from 'utils/localisation'

import { Cargo, CONTAINER_TYPE_22GP, CONTAINER_TYPE_42GP } from '../../Cargo'
import { TCarriageMode } from '../../Carriage'
import { QRCheckboxRow } from '../QRAdditional/QRCheckboxRow'
import { QrCellLeft, QrCellXs, QrRow } from '../QuoteRequestForm'
import {
  defaultFclCargo,
  defaultLclCargo,
  getLocationCountryCode,
  isRail,
  isSea,
  QuoteRequestFormState,
} from '../quoteRequestUtils'
import { FCLCargoRow } from './FCLCargoRow'
import { LCLCargoRow } from './LCLCargoRow'

const useStyles = makeStyles((theme: Theme) => ({
  addRow: {
    marginTop: theme.spacing(1),
  },
  addBtn: {
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  cargoTitle: {
    display: 'block',
    marginTop: theme.spacing(2),
  },
  requiredStar: {
    color: theme.palette.error.main,
  },
  goodsValueInput: {
    marginTop: theme.spacing(-0.5),
  },
  fclRecommendationQuickTipContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  fclRecommendationQuickTip: {
    paddingLeft: theme.spacing(1),
  },
}))

type TQrCargoProps = {
  hideLabel?: boolean
}

type CargoLimit = {
  mode: TCarriageMode
  maxVolume: number
  maxWeight: number
}

const cargoLimits: CargoLimit[] = [
  { mode: 'sea', maxVolume: 20, maxWeight: 4000 },
  { mode: 'rail', maxVolume: 10, maxWeight: 2000 },
]

export const isDateTodayOrLater = (date: Date | null): boolean => {
  if (!date) {
    return false
  }

  // Get today's date
  const today = new Date()

  // Set the time of today to midnight (00:00:00)
  today.setHours(0, 0, 0, 0)

  // Set the time of the input date to midnight (00:00:00)
  const inputDate = new Date(date)
  inputDate.setHours(0, 0, 0, 0)

  // Compare the input date with today's date
  return inputDate.getTime() >= today.getTime()
}

export const QRCargo: FC<TQrCargoProps> = ({ hideLabel }) => {
  const transportType = useWatch<QuoteRequestFormState, 'type'>({ name: 'type' })
  const transportModes = useWatch<QuoteRequestFormState, 'modes'>({ name: 'modes' })
  const cargoFclField = useWatch<QuoteRequestFormState, 'cargoFcl'>({ name: 'cargoFcl' })
  const cargoLclField = useWatch<QuoteRequestFormState, 'cargoLcl'>({ name: 'cargoLcl' })
  const { field: goodsValueField } = useController<QuoteRequestFormState, 'goodsValue'>({ name: 'goodsValue' })
  const { field: cargoStackableField } = useController<QuoteRequestFormState, 'cargoStackable'>({
    name: 'cargoStackable',
  })
  const destination = useWatch<QuoteRequestFormState, 'destination'>({ name: 'destination' })

  const {
    fields: fclCargoFields,
    append: appendFclCargo,
    remove: removeFclCargo,
  } = useFieldArray<QuoteRequestFormState, 'cargoFcl'>({ name: 'cargoFcl' })
  const {
    fields: lclCargoFields,
    append: appendLclCargo,
    remove: removeLclCargo,
  } = useFieldArray<QuoteRequestFormState, 'cargoLcl'>({ name: 'cargoLcl' })

  const isDestinationUSA = getLocationCountryCode(destination) === 'US'

  const { translate } = useI18n()
  const classes = useStyles()
  const { isEnabled } = useUnleashFeatureFlags()
  const [showFCLRecommendation, setShowFCLRecommendation] = useState(false)
  const isFCL = transportType === 'fcl'
  const isLCL = transportType === 'lcl'

  const showCRDForFCL = isEnabled(UnleashFeatureFlagKeys.QUOTE_REQUEST_FCL_CRD)
  const showCRD = (isFCL && showCRDForFCL) || isLCL

  const isBookingV2Enabled = !isEnabled(UnleashFeatureFlagKeys.BOOKINGS_EXCLUDE_CUSTOMERS)
  const isCRDRequired = isBookingV2Enabled || isLCL
  const { field: cargoReadyAtField } = useController<QuoteRequestFormState, 'cargoReadyAt'>({
    name: 'cargoReadyAt',
    rules: {
      required: showCRD && isCRDRequired,
      validate: isDateTodayOrLater,
    },
  })

  function hideFCLRecommendation() {
    setShowFCLRecommendation(false)
  }

  const lowerLimitBounds = cargoLimits.reduce(
    (lowerBounds, limit) => {
      if (transportModes.includes(limit.mode)) {
        const maxVolume = Math.min(lowerBounds.maxVolume, limit.maxVolume)
        const maxWeight = Math.min(lowerBounds.maxWeight, limit.maxWeight)
        return { maxWeight, maxVolume }
      }

      return lowerBounds
    },
    {
      maxWeight: Infinity,
      maxVolume: Infinity,
    }
  )

  const totalVolume = cargoLclField.reduce((accum, item) => {
    return accum + Number(item.volume) * Number(item.quantity)
  }, 0)

  const cargo: Cargo[] = isFCL ? cargoFclField : cargoLclField

  const totalWeight = cargo.reduce((accum, item) => {
    return accum + Number(item.grossWeight) * Number(item.quantity)
  }, 0)

  useEffect(() => {
    // With a disabled feature flag, the crd for fcl shipments should not be set because the feature is unstable and will time out in a lot of cases
    if (!showCRDForFCL && isFCL && cargoReadyAtField.value) {
      cargoReadyAtField.onChange(undefined)
    }
  }, [cargoReadyAtField.onChange, isFCL, showCRDForFCL])

  useEffect(() => {
    const hasRightMode = isSea(transportModes) || isRail(transportModes)

    if (
      hasRightMode &&
      isLCL &&
      (totalVolume > lowerLimitBounds.maxVolume || totalWeight > lowerLimitBounds.maxWeight)
    ) {
      setShowFCLRecommendation(true)
    } else {
      setShowFCLRecommendation(false)
    }
  }, [isLCL, lowerLimitBounds.maxVolume, lowerLimitBounds.maxWeight, totalVolume, totalWeight, transportModes])

  const handleAppend = () => {
    if (isFCL) {
      appendFclCargo({
        ...defaultFclCargo,
        containerType: isRail(transportModes) ? CONTAINER_TYPE_42GP : CONTAINER_TYPE_22GP,
      })
    } else {
      appendLclCargo(defaultLclCargo)
    }
  }

  return (
    <>
      {showFCLRecommendation && (
        <QrRow>
          <QrCellLeft />
          <QrCellXs>
            <StatusBox onClose={hideFCLRecommendation} variant="info" fullWidth>
              {translate('quote_request-fcl_recommendation', {
                cbm: lowerLimitBounds.maxVolume,
                weight: lowerLimitBounds.maxWeight,
              })}
              <br />
              <br />
              <div className={classes.fclRecommendationQuickTipContainer}>
                <EmojiObjectsIcon />
                <span className={classes.fclRecommendationQuickTip}>
                  <b>{translate('quote_request-fcl_recommendation-quick_tip_header')} </b>
                  {translate('quote_request-fcl_recommendation-quick_tip')}
                </span>
              </div>
            </StatusBox>
          </QrCellXs>
        </QrRow>
      )}
      <QrRow>
        {!hideLabel && (
          <QrCellLeft>
            <Typography className={classes.cargoTitle}>{translate('quote_request-cargo-title')}</Typography>
          </QrCellLeft>
        )}
        <Grid xs={hideLabel ? 12 : 10}>
          {isLCL
            ? lclCargoFields.map(({ id }, idx) => (
                <LCLCargoRow
                  key={id}
                  index={idx}
                  deleteCargo={cargoLclField.length > 1 ? () => removeLclCargo(idx) : null}
                />
              ))
            : fclCargoFields.map(({ id }, idx) => (
                <FCLCargoRow
                  key={id}
                  index={idx}
                  deleteCargo={cargoFclField.length > 1 ? () => removeFclCargo(idx) : null}
                  isDestinationUSA={isDestinationUSA}
                />
              ))}
          {isLCL && (
            <Grid container>
              <Grid xs={5} />
              <Grid xs={2}>
                <Typography color="textSecondary">{translate('quote_request-cargo-total_volume')}</Typography>
                <Typography variant="body1">
                  {formatNumber(totalVolume, 2)}
                  <small>&nbsp;cbm</small>
                </Typography>
              </Grid>
              <Grid xs={5}>
                <Typography color="textSecondary">{translate('quote_request-cargo-total_weight')}</Typography>
                <Typography variant="body1">
                  {formatNumber(totalWeight, 2)}
                  <small>&nbsp;kg</small>
                </Typography>
              </Grid>
            </Grid>
          )}

          <Grid container className={classes.addRow}>
            <Grid xs>
              {isLCL && (
                <Typography variant="caption" color="textSecondary">
                  {translate('quote_request-cargo-weight_volume_helper')}
                </Typography>
              )}
            </Grid>
            <Grid>
              <Button
                id="btn-add-cargo"
                className={classes.addBtn}
                onClick={handleAppend}
                disabled={isFCL && isRail(transportModes)}
              >
                {translate('quote_request-cargo-add')}
              </Button>
            </Grid>
          </Grid>

          {showCRD && (
            <DatePicker
              label={translate('quote_request-cargo-cargo_ready-title')}
              value={cargoReadyAtField.value}
              onChange={(date) => cargoReadyAtField.onChange(date)}
              disablePast
              slotProps={{
                openPickerIcon: {
                  'data-e2e-id': 'quote-request-cargo-ready-date-picker-button',
                },
                textField: {
                  required: isCRDRequired,
                  inputProps: {
                    'data-e2e-id': 'quote-request-cargo-ready-date-picker',
                  },
                },
              }}
            />
          )}

          {isLCL && isDestinationUSA && (
            <>
              <Grid container alignItems="baseline" spacing={2}>
                <Grid>
                  <TextField
                    label={translate('quote_request-goods-value')}
                    type="number"
                    inputProps={{ min: 1 }}
                    name="goods-value"
                    value={goodsValueField.value ? goodsValueField.value : ''}
                    onChange={(e) =>
                      goodsValueField.onChange({
                        value: Number(e.target.value),
                        code: 'EUR',
                      })
                    }
                    className={classes.goodsValueInput}
                  />
                </Grid>
              </Grid>
              <small>{translate('quote_request-goods-value-description')}</small>
            </>
          )}
        </Grid>
      </QrRow>
      {isLCL && (
        <QrRow>
          {!hideLabel && <QrCellLeft>{translate('quote_request-additional_information-title')}</QrCellLeft>}
          <QrCellXs>
            <QRCheckboxRow
              name="cargo-stackable"
              checked={!!cargoStackableField.value}
              onCheck={cargoStackableField.onChange}
              label={translate('quote_request-stackable_goods-title')}
              help={<small>{translate('quote_request-stackable_goods-description')}</small>}
            />
          </QrCellXs>
        </QrRow>
      )}
    </>
  )
}
